<script setup>
import { ref, computed, nextTick, onBeforeUnmount } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

// A short guided tour of the Squareme panel. Each step points at an element
// marked `data-squareme-tour="<target>"`; steps whose element is not on the
// page (for example activity when no customer matched) are skipped.
const props = defineProps({
  steps: { type: Array, required: true },
});

const emit = defineEmits(['finish']);

const HIGHLIGHT = ['ring-2', 'ring-n-brand', 'ring-offset-2', 'rounded-lg'];
const CARD_WIDTH = 288;
const GAP = 12;

const active = ref([]);
const index = ref(0);
const position = ref({});
let highlighted = null;

const isOpen = computed(() => active.value.length > 0);
const step = computed(() => active.value[index.value]);
const isLast = computed(() => index.value === active.value.length - 1);

const elementFor = target =>
  document.querySelector(`[data-squareme-tour="${target}"]`);

const clearHighlight = () => {
  highlighted?.classList.remove(...HIGHLIGHT);
  highlighted = null;
};

const show = async () => {
  clearHighlight();
  await nextTick();
  const element = elementFor(step.value.target);
  if (!element) return;
  element.scrollIntoView?.({ block: 'center' });
  element.classList.add(...HIGHLIGHT);
  highlighted = element;
  const rect = element.getBoundingClientRect();
  const left = Math.max(GAP, rect.left - CARD_WIDTH - GAP);
  const top = Math.min(
    Math.max(GAP, rect.top),
    Math.max(GAP, window.innerHeight - 220)
  );
  position.value = { top: `${top}px`, left: `${left}px` };
};

const start = () => {
  active.value = props.steps.filter(({ target }) => elementFor(target));
  index.value = 0;
  if (isOpen.value) show();
};

const finish = () => {
  clearHighlight();
  active.value = [];
  emit('finish');
};

const go = delta => {
  index.value += delta;
  show();
};

onBeforeUnmount(clearHighlight);

defineExpose({ start });
</script>

<template>
  <TeleportWithDirection v-if="isOpen" to="body">
    <div
      role="dialog"
      aria-modal="false"
      :aria-label="$t(step.title)"
      data-test-id="squareme-tour"
      class="fixed z-[70] flex w-72 flex-col gap-2 rounded-xl border border-n-weak bg-n-alpha-3 p-4 text-sm shadow-xl backdrop-blur-[100px]"
      :style="position"
    >
      <p class="mb-0 text-xs text-n-slate-11">
        {{
          $t('SQUAREME_PANEL.TOUR.STEP', {
            current: index + 1,
            total: active.length,
          })
        }}
      </p>
      <h3 class="mb-0 text-base font-medium text-n-slate-12">
        {{ $t(step.title) }}
      </h3>
      <p class="mb-0 text-n-slate-12">{{ $t(step.body) }}</p>
      <div class="flex items-center justify-between gap-2">
        <Button
          size="xs"
          variant="link"
          color="slate"
          :label="$t('SQUAREME_PANEL.TOUR.SKIP')"
          @click="finish"
        />
        <div class="flex gap-2">
          <Button
            v-if="index > 0"
            size="xs"
            variant="faded"
            color="slate"
            :label="$t('SQUAREME_PANEL.TOUR.BACK')"
            @click="go(-1)"
          />
          <Button
            size="xs"
            data-test-id="squareme-tour-next"
            :label="
              isLast
                ? $t('SQUAREME_PANEL.TOUR.DONE')
                : $t('SQUAREME_PANEL.TOUR.NEXT')
            "
            @click="isLast ? finish() : go(1)"
          />
        </div>
      </div>
    </div>
  </TeleportWithDirection>
</template>
